import { type RefObject, useCallback, useEffect, useRef } from "react";
import { debounce } from "@/lib/utils";
import type { CollapsedActivity } from "@/pages/accounts/account-explorer/touchpoints-table/types";

/**
 * Custom hook to track the visible range of activities in the table
 * Updates the parent component when the visible range changes
 */
export function useVisibleRange(
  tableRef: RefObject<HTMLDivElement>,
  collapsedData: CollapsedActivity[],
  onVisibleRangeChange?: (range: { start: string; end: string }) => void,
) {
  // Store the latest callback in a ref to avoid re-creating debounce function on every render
  const savedCallback = useRef(onVisibleRangeChange);

  useEffect(() => {
    savedCallback.current = onVisibleRangeChange;
  }, [onVisibleRangeChange]);

  const updateVisibleRange = useCallback(() => {
    const container = tableRef.current;
    if (!container) return;

    const tbody = container.querySelector("tbody");
    if (!tbody) return;

    const containerRect = container.getBoundingClientRect();
    const headerHeight =
      container.querySelector("thead")?.getBoundingClientRect().height || 0;
    const rows = tbody.querySelectorAll("tr");

    let firstRowInView: HTMLTableRowElement | null = null;
    let lastRowInView: HTMLTableRowElement | null = null;

    rows.forEach((row) => {
      if (row.getAttribute("data-gap-row") === "true") return; // Ignore gap rows

      const rowRect = row.getBoundingClientRect();
      const rowTop = rowRect.top - containerRect.top;
      const rowCenter = rowTop + rowRect.height / 2;

      // Check if the row's vertical center is within the visible area below the header
      const isCenterVisible =
        rowCenter >= headerHeight && rowCenter <= containerRect.height;

      if (isCenterVisible) {
        if (!firstRowInView) {
          firstRowInView = row as HTMLTableRowElement; // First row whose center is visible
        }
        lastRowInView = row as HTMLTableRowElement; // Update last row whose center is visible
      }
    });

    if (firstRowInView && lastRowInView && savedCallback.current) {
      const firstTimestamp = (firstRowInView as HTMLElement)?.getAttribute(
        "data-timestamp",
      );
      const lastTimestamp = (lastRowInView as HTMLElement)?.getAttribute(
        "data-timestamp",
      );

      if (firstTimestamp && lastTimestamp) {
        savedCallback.current({
          start: firstTimestamp,
          end: lastTimestamp,
        });
      }
    }
  }, [tableRef]); // Removed savedCallback.current from dependency array as it's stable via ref

  // Update visible range on scroll, debounced
  useEffect(() => {
    if (!tableRef.current || !collapsedData.length) return;

    const container = tableRef.current;
    // Debounce the update function - 150ms delay
    const debouncedUpdate = debounce(updateVisibleRange, 150);

    container.addEventListener("scroll", debouncedUpdate);
    // Call immediately once on setup
    updateVisibleRange();

    return () => {
      if (container) {
        container.removeEventListener("scroll", debouncedUpdate);
      }
    };
  }, [collapsedData, updateVisibleRange, tableRef]); // updateVisibleRange is stable due to useCallback

  return { updateVisibleRange }; // Returning the non-debounced version for potential direct calls if needed elsewhere
}
